import { useContext, useMemo } from "react";
import dayjs from "dayjs";
import { Button } from "antd";
import { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import { DictContext } from "@/layout/context/DictContext";
import { numFormmater } from "@/pages/Requirement/util";
import SText from "@/styles/SText";
import WMSAvatar from "@/styles/WMSAvatar";
import { EquipmentEnumItem, EquipmentRecord } from "@/types/equipment";
import { User } from "@/types/user";
import { dateFormat } from "@/utils/format";
import { getUserAvatarTip } from "@/utils/user";

// 设备记录列 Hook 所需的记录类型枚举与详情回调。
interface UseEquipmentRecordColumnProps {
  recordTypeItems: EquipmentEnumItem[];
  onDetail: (id: string) => void;
}

// 文本与数值列统一处理空值并限制内容宽度。
const renderText = (value?: string | number | null) => (
  <SText
    text={value === null || value === undefined || value === ""
      ? "-"
      : String(value)}
    style={{ width: "100%" }}
  />
);

// 时间列统一转换为带时间的本地展示格式。
const renderTime = (value?: number | null) => (
  renderText(value === null || value === undefined
    ? null
    : dateFormat(dayjs(value), true))
);

// 用户字段只展示头像，姓名和手机号由 WMSAvatar 的悬浮提示提供。
const renderUserAvatar = (user?: User | null) => (
  user ? (
    <WMSAvatar
      creator={user}
      uniChar={user.id}
      avatarId={user.avatar}
      alt={getUserAvatarTip(user)}
      size={24}
    />
  ) : "-"
);

// 生成设备记录流水列，统一关联枚举、用户和币种文案。
const useEquipmentRecordColumn = ({
  recordTypeItems,
  onDetail,
}: UseEquipmentRecordColumnProps): ColumnsType<EquipmentRecord> => {
  const { t } = useTranslation();
  const { currencys } = useContext(DictContext);

  return useMemo<ColumnsType<EquipmentRecord>>(() => {
    // 预先建立类型索引，避免每个单元格重复遍历枚举列表。
    const recordTypeLabels = new Map(
      recordTypeItems.map(({ v, desc }) => [v, desc]),
    );

    return [
      {
        title: t("equipment.recordType"),
        dataIndex: "recordType",
        width: 90,
        render: (value: number) => renderText(
          recordTypeLabels.get(value) ?? value,
        ),
      },
      {
        title: t("equipment.occurredAt"),
        dataIndex: "occurredAt",
        width: 135,
        render: renderTime,
      },
      {
        title: t("equipment.operator"),
        dataIndex: "operatorUser",
        width: 40,
        render: (_, record) => renderUserAvatar(record.operatorUser),
      },
      {
        title: t("equipment.borrower"),
        dataIndex: "borrowerUser",
        width: 40,
        render: (_, record) => renderUserAvatar(record.borrowerUser),
      },
      {
        title: t("equipment.expectedReturnAt"),
        dataIndex: "expectedReturnAt",
        width: 135,
        render: renderTime,
      },
      {
        title: t("equipment.returnAt"),
        dataIndex: "returnAt",
        width: 135,
        render: renderTime,
      },
      {
        title: t("equipment.recordCost"),
        dataIndex: "cost",
        width: 50,
        render: (value: number | null) => renderText(
          value === null ? null : numFormmater(value),
        ),
      },
      // 币种优先使用字典文案，未匹配时保留后端原始值。
      {
        title: t("equipment.recordCostCurrency"),
        dataIndex: "costCurrency",
        width: 30,
        render: (value: number | null) => renderText(
          value === null
            ? null
            : currencys.find(({ key }) => String(key) === String(value))?.desc
              ?? value,
        ),
      },
      {
        title: t("equipment.odometer"),
        dataIndex: "odometer",
        width: 80,
        render: renderText,
      },
      {
        title: t("equipment.vendor"),
        dataIndex: "vendor",
        width: 120,
        render: renderText,
      },
      {
        title: t("equipment.recordComment"),
        dataIndex: "comment",
        width: 180,
        render: renderText,
      },
      {
        title: t("equipment.createdAt"),
        dataIndex: "createdAt",
        width: 135,
        render: renderTime,
      },
      {
        title: t("equipment.actions"),
        key: "action",
        fixed: "right",
        width: 64,
        render: (_, record) => (
          <Button
            type="link"
            size="small"
            onClick={() => onDetail(record.id)}
          >
            {t("common.detail")}
          </Button>
        ),
      },
    ];
  }, [currencys, onDetail, recordTypeItems, t]);
};

export default useEquipmentRecordColumn;
